// Client-side template processor for live preview
// This mirrors the server-side engine but runs in the browser

export interface ProcessorContext {
  data: Record<string, any>;
  clauses: Record<string, string>;
  jurisdiction?: string;
}

export interface ProcessorResult {
  content: string;
  errors: ProcessorError[];
  warnings: ProcessorWarning[];
  usedVariables: string[];
  missingVariables: string[];
}

export interface ProcessorError {
  type: 'syntax' | 'runtime' | 'reference';
  message: string;
  line?: number;
  column?: number;
}

export interface ProcessorWarning {
  type: 'missing_variable' | 'unused_clause' | 'deprecated';
  message: string;
  variable?: string;
}

// Token types for the lexer
type TokenType = 
  | 'TEXT' 
  | 'VARIABLE' 
  | 'IF' 
  | 'ELSEIF' 
  | 'ELSE' 
  | 'ENDIF' 
  | 'EACH' 
  | 'ENDEACH' 
  | 'CLAUSE'
  | 'COMMENT';

interface Token {
  type: TokenType;
  value: string;
  raw: string;
  line: number;
  column: number;
}

// Built-in filters
const FILTERS: Record<string, (value: any, ...args: string[]) => string> = {
  uppercase: (v) => String(v).toUpperCase(),
  lowercase: (v) => String(v).toLowerCase(),
  capitalize: (v) => String(v).charAt(0).toUpperCase() + String(v).slice(1).toLowerCase(),
  titlecase: (v) => String(v).replace(/\b\w/g, c => c.toUpperCase()),
  
  currency: (v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return v;
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(num);
  },
  
  number: (v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return v;
    return new Intl.NumberFormat('en-US').format(num);
  },
  
  words: (v) => {
    const num = parseFloat(v);
    if (isNaN(num)) return v;
    return numberToWords(num);
  },
  
  ordinal: (v) => {
    const num = parseInt(v);
    if (isNaN(num)) return v;
    const suffixes = ['th', 'st', 'nd', 'rd'];
    const remainder = num % 100;
    return num + (suffixes[(remainder - 20) % 10] || suffixes[remainder] || suffixes[0]);
  },
  
  date: (v, format = 'long') => {
    const date = new Date(v);
    if (isNaN(date.getTime())) return v;
    
    const options: Intl.DateTimeFormatOptions = 
      format === 'short' ? { month: 'numeric', day: 'numeric', year: 'numeric' } :
      format === 'medium' ? { month: 'short', day: 'numeric', year: 'numeric' } :
      { month: 'long', day: 'numeric', year: 'numeric' };
    
    return date.toLocaleDateString('en-US', options);
  },
  
  legaldate: (v) => {
    const date = new Date(v);
    if (isNaN(date.getTime())) return v;
    const day = date.getDate();
    const ordinal = FILTERS.ordinal(day);
    const month = date.toLocaleDateString('en-US', { month: 'long' });
    const year = date.getFullYear();
    return `${ordinal} day of ${month}, ${year}`;
  },
  
  legalname: (v) => {
    if (typeof v === 'object' && v !== null) {
      const parts = [v.firstName, v.middleName, v.lastName].filter(Boolean);
      return parts.join(' ').toUpperCase();
    }
    return String(v).toUpperCase();
  },
  
  possessive: (v) => {
    const str = String(v);
    return str.endsWith('s') ? `${str}'` : `${str}'s`;
  },
  
  plural: (v, singular, plural) => {
    const num = parseInt(v);
    return num === 1 ? singular : (plural || singular + 's');
  },
  
  default: (v, defaultValue) => {
    return v === null || v === undefined || v === '' ? defaultValue : v;
  },
  
  json: (v) => JSON.stringify(v, null, 2),
  
  trim: (v) => String(v).trim(),
  
  truncate: (v, length = '100') => {
    const str = String(v);
    const len = parseInt(length);
    return str.length > len ? str.slice(0, len) + '...' : str;
  },
  
  replace: (v, search, replacement = '') => {
    return String(v).replace(new RegExp(search, 'g'), replacement);
  },
  
  phone: (v) => {
    const digits = String(v).replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    return v;
  },
  
  ssn: (v) => {
    const digits = String(v).replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 3)}-${digits.slice(3, 5)}-${digits.slice(5)}`;
    }
    return v;
  },
  
  ein: (v) => {
    const digits = String(v).replace(/\D/g, '');
    if (digits.length === 9) {
      return `${digits.slice(0, 2)}-${digits.slice(2)}`;
    }
    return v;
  },
};

// Number to words conversion
function numberToWords(num: number): string {
  if (num === 0) return 'zero';
  
  const ones = ['', 'one', 'two', 'three', 'four', 'five', 'six', 'seven', 'eight', 'nine',
    'ten', 'eleven', 'twelve', 'thirteen', 'fourteen', 'fifteen', 'sixteen', 'seventeen', 'eighteen', 'nineteen'];
  const tens = ['', '', 'twenty', 'thirty', 'forty', 'fifty', 'sixty', 'seventy', 'eighty', 'ninety'];
  const scales = ['', 'thousand', 'million', 'billion', 'trillion'];
  
  const convertGroup = (n: number): string => {
    if (n === 0) return '';
    if (n < 20) return ones[n];
    if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? '-' + ones[n % 10] : '');
    return ones[Math.floor(n / 100)] + ' hundred' + (n % 100 ? ' ' + convertGroup(n % 100) : '');
  };
  
  const isNegative = num < 0;
  num = Math.abs(Math.floor(num));
  
  const groups: string[] = [];
  let scaleIndex = 0;
  
  while (num > 0) {
    const group = num % 1000;
    if (group > 0) {
      const groupWords = convertGroup(group);
      groups.unshift(scales[scaleIndex] ? `${groupWords} ${scales[scaleIndex]}` : groupWords);
    }
    num = Math.floor(num / 1000);
    scaleIndex++;
  }
  
  const result = groups.join(' ');
  return isNegative ? `negative ${result}` : result;
}

// Tokenizer
function tokenize(template: string): Token[] {
  const tokens: Token[] = [];
  let pos = 0;
  let line = 1;
  let column = 1;
  
  const patterns = [
    { type: 'COMMENT', regex: /^\{\{!--[\s\S]*?--\}\}/ },
    { type: 'IF', regex: /^\{\{#if\s+(.+?)\}\}/ },
    { type: 'ELSEIF', regex: /^\{\{#elseif\s+(.+?)\}\}/ },
    { type: 'ELSE', regex: /^\{\{#else\}\}/ },
    { type: 'ENDIF', regex: /^\{\{\/if\}\}/ },
    { type: 'EACH', regex: /^\{\{#each\s+(.+?)\s+as\s+(\w+)(?:\s*,\s*(\w+))?\}\}/ },
    { type: 'ENDEACH', regex: /^\{\{\/each\}\}/ },
    { type: 'CLAUSE', regex: /^\{\{@clause:([^:}]+)(?::([^}]+))?\}\}/ },
    { type: 'VARIABLE', regex: /^\{\{([^#@/!].*?)\}\}/ },
  ];
  
  while (pos < template.length) {
    let matched = false;
    
    for (const { type, regex } of patterns) {
      const match = template.slice(pos).match(regex);
      if (match) {
        if (type !== 'COMMENT') {
          tokens.push({
            type: type as TokenType,
            value: match[1] || '',
            raw: match[0],
            line,
            column,
          });
        }
        
        // Update position
        for (const char of match[0]) {
          if (char === '\n') {
            line++;
            column = 1;
          } else {
            column++;
          }
        }
        pos += match[0].length;
        matched = true;
        break;
      }
    }
    
    if (!matched) {
      // Collect text until next tag
      let text = '';
      const startLine = line;
      const startColumn = column;
      
      while (pos < template.length && template.slice(pos, pos + 2) !== '{{') {
        const char = template[pos];
        text += char;
        if (char === '\n') {
          line++;
          column = 1;
        } else {
          column++;
        }
        pos++;
      }
      
      if (text) {
        tokens.push({
          type: 'TEXT',
          value: text,
          raw: text,
          line: startLine,
          column: startColumn,
        });
      }
    }
  }
  
  return tokens;
}

// Get nested value from object using dot notation
function getValue(obj: any, path: string): any {
  const parts = path.split('.');
  let current = obj;
  
  for (const part of parts) {
    if (current === null || current === undefined) return undefined;
    
    // Handle array index: items[0]
    const arrayMatch = part.match(/^(\w+)\[(\d+)\]$/);
    if (arrayMatch) {
      current = current[arrayMatch[1]];
      if (Array.isArray(current)) {
        current = current[parseInt(arrayMatch[2])];
      } else {
        return undefined;
      }
    } else {
      current = current[part];
    }
  }
  
  return current;
}

// Apply filter to value
function applyFilter(value: any, filterStr: string): string {
  const match = filterStr.match(/^(\w+)(?::["']?([^"']+)["']?(?:,["']?([^"']+)["']?)?)?$/);
  if (!match) return String(value);
  
  const [, filterName, arg1, arg2] = match;
  const filter = FILTERS[filterName];
  
  if (!filter) {
    console.warn(`Unknown filter: ${filterName}`);
    return String(value);
  }
  
  return filter(value, arg1, arg2);
}

// Process a variable expression
function processVariable(expr: string, context: ProcessorContext): { value: string; missing: boolean } {
  const parts = expr.trim().split('|').map(p => p.trim());
  const varPath = parts[0];
  const filters = parts.slice(1);
  
  let value = getValue(context.data, varPath);
  const missing = value === undefined || value === null;
  
  if (missing) {
    return { value: `[${varPath}]`, missing: true };
  }
  
  // Apply filters
  let result = value;
  for (const filter of filters) {
    result = applyFilter(result, filter);
  }
  
  return { value: String(result), missing: false };
}

// Evaluate a condition
function evaluateCondition(expr: string, context: ProcessorContext): boolean {
  const trimmed = expr.trim();
  
  // Handle comparison operators
  const comparisonMatch = trimmed.match(/^(.+?)\s*(===?|!==?|>=?|<=?)\s*(.+)$/);
  if (comparisonMatch) {
    const [, left, op, right] = comparisonMatch;
    const leftVal = evaluateValue(left.trim(), context);
    const rightVal = evaluateValue(right.trim(), context);
    
    switch (op) {
      case '==':
      case '===':
        return leftVal === rightVal;
      case '!=':
      case '!==':
        return leftVal !== rightVal;
      case '>':
        return leftVal > rightVal;
      case '>=':
        return leftVal >= rightVal;
      case '<':
        return leftVal < rightVal;
      case '<=':
        return leftVal <= rightVal;
    }
  }
  
  // Handle logical operators
  if (trimmed.includes(' and ') || trimmed.includes(' && ')) {
    const parts = trimmed.split(/\s+(?:and|&&)\s+/);
    return parts.every(p => evaluateCondition(p, context));
  }
  
  if (trimmed.includes(' or ') || trimmed.includes(' || ')) {
    const parts = trimmed.split(/\s+(?:or|\|\|)\s+/);
    return parts.some(p => evaluateCondition(p, context));
  }
  
  // Handle negation
  if (trimmed.startsWith('!') || trimmed.startsWith('not ')) {
    const inner = trimmed.replace(/^(!|not\s+)/, '');
    return !evaluateCondition(inner, context);
  }
  
  // Simple truthy check
  const value = getValue(context.data, trimmed);
  return !!value && value !== '' && (Array.isArray(value) ? value.length > 0 : true);
}

// Evaluate a value (variable or literal)
function evaluateValue(expr: string, context: ProcessorContext): any {
  const trimmed = expr.trim();
  
  // String literal
  if ((trimmed.startsWith('"') && trimmed.endsWith('"')) || 
      (trimmed.startsWith("'") && trimmed.endsWith("'"))) {
    return trimmed.slice(1, -1);
  }
  
  // Number literal
  if (/^-?\d+\.?\d*$/.test(trimmed)) {
    return parseFloat(trimmed);
  }
  
  // Boolean literals
  if (trimmed === 'true') return true;
  if (trimmed === 'false') return false;
  if (trimmed === 'null' || trimmed === 'undefined') return null;
  
  // Variable reference
  return getValue(context.data, trimmed);
}

// Main processor class
export class TemplateProcessor {
  private context: ProcessorContext;
  private errors: ProcessorError[] = [];
  private warnings: ProcessorWarning[] = [];
  private usedVariables: Set<string> = new Set();
  private missingVariables: Set<string> = new Set();
  
  constructor(context: ProcessorContext) {
    this.context = context;
  }
  
  process(template: string): ProcessorResult {
    this.errors = [];
    this.warnings = [];
    this.usedVariables.clear();
    this.missingVariables.clear();
    
    try {
      const tokens = tokenize(template);
      const content = this.processTokens(tokens, 0, tokens.length);
      
      return {
        content,
        errors: this.errors,
        warnings: this.warnings,
        usedVariables: Array.from(this.usedVariables),
        missingVariables: Array.from(this.missingVariables),
      };
    } catch (err: any) {
      this.errors.push({
        type: 'runtime',
        message: err.message || 'Unknown error during processing',
      });
      
      return {
        content: '',
        errors: this.errors,
        warnings: this.warnings,
        usedVariables: Array.from(this.usedVariables),
        missingVariables: Array.from(this.missingVariables),
      };
    }
  }
  
  private processTokens(tokens: Token[], start: number, end: number): string {
    let result = '';
    let i = start;
    
    while (i < end) {
      const token = tokens[i];
      
      switch (token.type) {
        case 'TEXT':
          result += token.value;
          i++;
          break;
          
        case 'VARIABLE':
          const { value, missing } = processVariable(token.value, this.context);
          const varName = token.value.split('|')[0].trim();
          this.usedVariables.add(varName);
          if (missing) {
            this.missingVariables.add(varName);
            this.warnings.push({
              type: 'missing_variable',
              message: `Missing variable: ${varName}`,
              variable: varName,
            });
          }
          result += value;
          i++;
          break;
          
        case 'IF':
          const { content: ifContent, endIndex: ifEnd } = this.processConditional(tokens, i, end);
          result += ifContent;
          i = ifEnd + 1;
          break;
          
        case 'EACH':
          const { content: eachContent, endIndex: eachEnd } = this.processLoop(tokens, i, end);
          result += eachContent;
          i = eachEnd + 1;
          break;
          
        case 'CLAUSE':
          result += this.processClause(token);
          i++;
          break;
          
        default:
          i++;
      }
    }
    
    return result;
  }
  
  private processConditional(tokens: Token[], start: number, end: number): { content: string; endIndex: number } {
    const branches: { condition: string | null; start: number; end: number }[] = [];
    let depth = 0;
    let currentBranchStart = start + 1;
    let currentCondition: string | null = tokens[start].value;
    
    for (let i = start; i < end; i++) {
      const token = tokens[i];
      
      if (token.type === 'IF') {
        if (i !== start) depth++;
      } else if (token.type === 'ENDIF') {
        if (depth === 0) {
          branches.push({ condition: currentCondition, start: currentBranchStart, end: i });
          
          // Evaluate branches
          for (const branch of branches) {
            if (branch.condition === null || evaluateCondition(branch.condition, this.context)) {
              return {
                content: this.processTokens(tokens, branch.start, branch.end),
                endIndex: i,
              };
            }
          }
          
          return { content: '', endIndex: i };
        }
        depth--;
      } else if (depth === 0) {
        if (token.type === 'ELSEIF') {
          branches.push({ condition: currentCondition, start: currentBranchStart, end: i });
          currentCondition = token.value;
          currentBranchStart = i + 1;
        } else if (token.type === 'ELSE') {
          branches.push({ condition: currentCondition, start: currentBranchStart, end: i });
          currentCondition = null; // null means always true (else branch)
          currentBranchStart = i + 1;
        }
      }
    }
    
    this.errors.push({
      type: 'syntax',
      message: 'Unclosed if block',
      line: tokens[start].line,
    });
    
    return { content: '', endIndex: end };
  }
  
  private processLoop(tokens: Token[], start: number, end: number): { content: string; endIndex: number } {
    const token = tokens[start];
    const match = token.raw.match(/\{\{#each\s+(.+?)\s+as\s+(\w+)(?:\s*,\s*(\w+))?\}\}/);
    
    if (!match) {
      this.errors.push({
        type: 'syntax',
        message: 'Invalid each syntax',
        line: token.line,
      });
      return { content: '', endIndex: start };
    }
    
    const [, arrayPath, itemVar, indexVar] = match;
    const array = getValue(this.context.data, arrayPath);
    
    // Find matching ENDEACH
    let depth = 0;
    let endIndex = start;
    
    for (let i = start; i < end; i++) {
      if (tokens[i].type === 'EACH') depth++;
      else if (tokens[i].type === 'ENDEACH') {
        depth--;
        if (depth === 0) {
          endIndex = i;
          break;
        }
      }
    }
    
    if (endIndex === start) {
      this.errors.push({
        type: 'syntax',
        message: 'Unclosed each block',
        line: token.line,
      });
      return { content: '', endIndex: end };
    }
    
    if (!Array.isArray(array)) {
      this.warnings.push({
        type: 'missing_variable',
        message: `Expected array for loop: ${arrayPath}`,
        variable: arrayPath,
      });
      return { content: '', endIndex };
    }
    
    // Process loop body for each item
    let result = '';
    const originalData = { ...this.context.data };
    
    for (let idx = 0; idx < array.length; idx++) {
      this.context.data[itemVar] = array[idx];
      if (indexVar) {
        this.context.data[indexVar] = idx;
      }
      
      // Add loop metadata
      this.context.data['@first'] = idx === 0;
      this.context.data['@last'] = idx === array.length - 1;
      this.context.data['@index'] = idx;
      this.context.data['@count'] = array.length;
      
      result += this.processTokens(tokens, start + 1, endIndex);
    }
    
    // Restore original data
    this.context.data = originalData;
    
    return { content: result, endIndex };
  }
  
  private processClause(token: Token): string {
    const match = token.raw.match(/\{\{@clause:([^:}]+)(?::([^}]+))?\}\}/);
    if (!match) return '';
    
    const [, clauseId, jurisdiction] = match;
    const effectiveJurisdiction = jurisdiction || this.context.jurisdiction;
    
    // Look up clause content
    let clauseContent = this.context.clauses[clauseId];
    
    if (!clauseContent) {
      this.warnings.push({
        type: 'unused_clause',
        message: `Clause not found: ${clauseId}`,
      });
      return `[Clause: ${clauseId}]`;
    }
    
    // Recursively process clause content (clauses can contain variables)
    const subProcessor = new TemplateProcessor(this.context);
    const result = subProcessor.process(clauseContent);
    
    // Merge errors/warnings
    this.errors.push(...result.errors);
    this.warnings.push(...result.warnings);
    result.usedVariables.forEach(v => this.usedVariables.add(v));
    result.missingVariables.forEach(v => this.missingVariables.add(v));
    
    return result.content;
  }
}

// Convenience function
export function processTemplate(
  template: string,
  data: Record<string, any>,
  clauses: Record<string, string> = {},
  jurisdiction?: string
): ProcessorResult {
  const processor = new TemplateProcessor({ data, clauses, jurisdiction });
  return processor.process(template);
}

// Extract all variables from a template
export function extractVariables(template: string): string[] {
  const variables = new Set<string>();
  const regex = /\{\{([^#@/!].*?)\}\}/g;
  let match;
  
  while ((match = regex.exec(template)) !== null) {
    const varExpr = match[1].split('|')[0].trim();
    // Skip loop variables and metadata
    if (!varExpr.startsWith('@') && !varExpr.includes(' ')) {
      variables.add(varExpr.split('.')[0]); // Get root variable
    }
  }
  
  return Array.from(variables);
}

// Validate template syntax
export function validateTemplate(template: string): ProcessorError[] {
  const errors: ProcessorError[] = [];
  
  // Check for balanced blocks
  const ifCount = (template.match(/\{\{#if\s/g) || []).length;
  const endifCount = (template.match(/\{\{\/if\}\}/g) || []).length;
  if (ifCount !== endifCount) {
    errors.push({
      type: 'syntax',
      message: `Unbalanced if blocks: ${ifCount} opens, ${endifCount} closes`,
    });
  }
  
  const eachCount = (template.match(/\{\{#each\s/g) || []).length;
  const endeachCount = (template.match(/\{\{\/each\}\}/g) || []).length;
  if (eachCount !== endeachCount) {
    errors.push({
      type: 'syntax',
      message: `Unbalanced each blocks: ${eachCount} opens, ${endeachCount} closes`,
    });
  }
  
  // Check for invalid syntax
  const invalidTags = template.match(/\{\{[^}]*$/gm);
  if (invalidTags) {
    errors.push({
      type: 'syntax',
      message: 'Unclosed template tag found',
    });
  }
  
  return errors;
}
